import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Loader2, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { EmptyState, ErrorState } from '@/components/shared/states';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { PeopleSubnav } from '@/pages/people/people-subnav';
import { useChurchStore } from '@/stores/church-store';
import { formatNumber } from '@/utils/format';

export interface MemberDuplicateScanMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  memberType: string;
  status: string;
  linked: boolean;
  createdAt: string;
}

export type MemberDuplicateMatchReason = 'email' | 'phone' | 'name';

export interface MemberDuplicateGroup {
  matchReasons: MemberDuplicateMatchReason[];
  members: MemberDuplicateScanMember[];
}

interface MemberDuplicateScanResult {
  scannedMemberCount: number;
  duplicateGroupCount: number;
  groups: MemberDuplicateGroup[];
}

const displayName = (row: MemberDuplicateScanMember) =>
  [row.firstName, row.lastName].filter(Boolean).join(' ');

const matchTone: Record<MemberDuplicateMatchReason, string> = {
  email: 'border-amber-300/80 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20',
  phone: 'border-orange-300/80 bg-orange-50/50 dark:border-orange-800 dark:bg-orange-950/20',
  name: 'border-sky-300/80 bg-sky-50/50 dark:border-sky-800 dark:bg-sky-950/20',
};

const buildMergePairs = (groups: MemberDuplicateGroup[]) => {
  const merges: Array<{ survivorId: string; duplicateId: string }> = [];
  for (const group of groups) {
    const survivorId = group.members[0]?.id;
    if (!survivorId) continue;
    for (const row of group.members) {
      if (row.id !== survivorId) {
        merges.push({ survivorId, duplicateId: row.id });
      }
    }
  }
  return merges;
};

const DuplicateGroupCard = ({
  group,
  groupIndex,
  canManage,
  canDelete,
  onRefresh,
}: {
  group: MemberDuplicateGroup;
  groupIndex: number;
  canManage: boolean;
  canDelete: boolean;
  onRefresh: () => void;
}) => {
  const { t } = useTranslation('people');
  const { t: tErrors } = useTranslation('errors');
  const { t: tCommon } = useTranslation();
  const [survivorId, setSurvivorId] = useState(group.members[0]?.id ?? '');
  const [deleteTarget, setDeleteTarget] = useState<MemberDuplicateScanMember | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const primaryReason = group.matchReasons[0] ?? 'name';

  const merge = useMutation({
    mutationFn: (duplicateId: string) =>
      api.post<{ survivorId: string }>(`/people/members/${survivorId}/merge`, { duplicateId }),
    onSuccess: () => {
      toast.success(t('duplicates.mergeSuccess'));
      onRefresh();
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? tErrors(error.code, error.params) : tErrors('UNEXPECTED'));
    },
  });

  const remove = useMutation({
    mutationFn: (memberId: string) => api.delete(`/people/members/${memberId}`),
    onSuccess: () => {
      toast.success(t('duplicates.deleteSuccess'));
      setDeleteTarget(null);
      onRefresh();
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? tErrors(error.code, error.params) : tErrors('UNEXPECTED'));
    },
  });

  const duplicateIds = group.members.filter((row) => row.id !== survivorId).map((row) => row.id);

  const columns: DataTableColumn<MemberDuplicateScanMember>[] = [
    {
      id: 'keep',
      header: t('duplicates.columns.keep'),
      cell: (row) => (
        <input
          type="radio"
          name={`survivor-${groupIndex}`}
          checked={survivorId === row.id}
          onChange={() => setSurvivorId(row.id)}
          aria-label={t('duplicates.keepRecord', { name: displayName(row) })}
        />
      ),
    },
    {
      id: 'name',
      header: t('members.columns.name'),
      cell: (row) => (
        <MemberNameLink
          memberId={row.id}
          name={displayName(row)}
          showPhoto
          className="font-medium"
        />
      ),
    },
    {
      id: 'type',
      header: t('members.columns.type'),
      cell: (row) => t(`members.type.${row.memberType}`),
    },
    {
      id: 'contact',
      header: t('duplicates.columns.contact'),
      cell: (row) => [row.email, row.phone].filter(Boolean).join(' · ') || '—',
    },
    {
      id: 'status',
      header: t('members.columns.status'),
      cell: (row) => (
        <Badge variant={row.status === 'active' ? 'success' : 'neutral'}>
          {t(`members.status.${row.status}`)}
        </Badge>
      ),
    },
    {
      id: 'linked',
      header: t('duplicates.columns.linked'),
      cell: (row) => (row.linked ? t('duplicates.linkedYes') : '—'),
    },
    {
      id: 'actions',
      header: '',
      cell: (row) =>
        canDelete && row.id !== survivorId ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setDeleteTarget(row)}
            disabled={remove.isPending}
          >
            {t('duplicates.deleteOne')}
          </Button>
        ) : null,
    },
  ];

  const matchDescription =
    group.matchReasons.length === 1
      ? t(`duplicates.matchReasons.${group.matchReasons[0]!}`)
      : t('duplicates.matchReasons.combined', {
          reasons: group.matchReasons
            .map((reason) => t(`duplicates.matchReasonLabels.${reason}`))
            .join(t('duplicates.matchReasonLabels.joiner')),
        });

  return (
    <>
      <Card className={matchTone[primaryReason]}>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">
                {t('duplicates.groupTitle', { index: groupIndex + 1 })}
              </CardTitle>
              <p className="mt-1 text-body-sm text-fg-secondary">{matchDescription}</p>
            </div>
            <div className="flex flex-wrap gap-1">
              {group.matchReasons.map((reason) => (
                <Badge key={reason} variant="neutral">
                  {t(`duplicates.matchTypes.${reason}`)}
                </Badge>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <DataTable rows={group.members} columns={columns} rowKey={(row) => row.id} />
          {canManage && duplicateIds.length > 0 ? (
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                loading={merge.isPending}
                onClick={() => setMergeOpen(true)}
              >
                {t('duplicates.mergeIntoKeep', { count: duplicateIds.length })}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={mergeOpen}
        onOpenChange={setMergeOpen}
        title={t('duplicates.mergeConfirmTitle')}
        description={t('duplicates.mergeConfirmDescription', {
          keepName: displayName(group.members.find((row) => row.id === survivorId) ?? group.members[0]!),
          count: duplicateIds.length,
        })}
        confirmLabel={t('duplicates.mergeAction')}
        cancelLabel={tCommon('action.cancel')}
        onConfirm={async () => {
          for (const duplicateId of duplicateIds) {
            await merge.mutateAsync(duplicateId);
          }
          setMergeOpen(false);
        }}
        loading={merge.isPending}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t('duplicates.deleteConfirmTitle')}
        description={t('duplicates.deleteConfirmDescription', {
          name: deleteTarget ? displayName(deleteTarget) : '',
        })}
        confirmLabel={t('duplicates.deleteAction')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
        }}
        loading={remove.isPending}
      />
    </>
  );
};

export const MemberDuplicatesPage = () => {
  const { t } = useTranslation('people');
  const { t: tErrors } = useTranslation('errors');
  const { t: tCommon } = useTranslation();
  const [searchParams] = useSearchParams();
  const membership = useChurchStore((state) => state.membership);
  const queryClient = useQueryClient();
  const autoRun = searchParams.get('run') === '1';
  const importedMemberIds = searchParams.get('memberIds')?.split(',').filter(Boolean) ?? [];

  const canManage = hasPermission(membership, PERMISSIONS.PEOPLE_EDIT);
  const canDelete = hasPermission(membership, PERMISSIONS.PEOPLE_DELETE);

  const [shouldRun, setShouldRun] = useState(autoRun);
  const [mergeAllOpen, setMergeAllOpen] = useState(false);

  const scan = useQuery({
    queryKey: ['people', 'duplicates', importedMemberIds.join(',')],
    queryFn: () =>
      api.post<MemberDuplicateScanResult>('/people/members/duplicate-check', {
        memberIds: importedMemberIds.length > 0 ? importedMemberIds : undefined,
      }),
    enabled: shouldRun,
  });

  const bulkMerge = useMutation({
    mutationFn: (merges: Array<{ survivorId: string; duplicateId: string }>) =>
      api.post<{ merged: string[]; failed: Array<{ duplicateId: string; code: string }> }>(
        '/people/members/bulk/merge',
        { merges },
      ),
    onSuccess: (result) => {
      toast.success(t('duplicates.bulkMerged', { count: result.merged.length }));
      setMergeAllOpen(false);
      refresh();
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? tErrors(error.code, error.params) : tErrors('UNEXPECTED'));
    },
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['people', 'duplicates'] });
    void scan.refetch();
    void queryClient.invalidateQueries({ queryKey: ['people', 'members'] });
  };

  const mergePairs = useMemo(
    () => (scan.data ? buildMergePairs(scan.data.groups) : []),
    [scan.data],
  );

  const summary = useMemo(() => {
    if (!scan.data) return null;
    return [
      { label: t('duplicates.summary.scanned'), value: formatNumber(scan.data.scannedMemberCount) },
      { label: t('duplicates.summary.groups'), value: formatNumber(scan.data.duplicateGroupCount) },
    ];
  }, [scan.data, t]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('duplicates.title')}
        description={t('duplicates.description')}
        breadcrumbs={[
          { label: t('members.title'), to: '/people' },
          { label: t('duplicates.title') },
        ]}
      />
      <PeopleSubnav />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('duplicates.runTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-body-sm text-fg-secondary">{t('duplicates.runHint')}</p>
          <Button
            type="button"
            onClick={() => setShouldRun(true)}
            loading={scan.isFetching}
            disabled={scan.isFetching}
          >
            {scan.isFetching ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Search className="mr-2 size-4" />
            )}
            {t('duplicates.runCheck')}
          </Button>
        </CardContent>
      </Card>

      {scan.isError ? (
        <ErrorState
          title={t('duplicates.errorTitle')}
          body={t('duplicates.errorDescription')}
          onRetry={() => void scan.refetch()}
        />
      ) : null}

      {shouldRun && scan.data && summary ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            {summary.map((item) => (
              <Card key={item.label}>
                <CardContent className="pt-6">
                  <p className="text-caption text-fg-secondary">{item.label}</p>
                  <p className="text-heading-sm font-semibold tabular-nums">{item.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {scan.data.groups.length === 0 ? (
            <EmptyState
              icon={<Copy className="size-8" aria-hidden />}
              title={t('duplicates.emptyTitle')}
              body={t('duplicates.emptyDescription')}
            />
          ) : (
            <div className="space-y-4">
              {canDelete && mergePairs.length > 0 ? (
                <div className="flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setMergeAllOpen(true)}>
                    {t('duplicates.mergeAllGroups', { count: scan.data.groups.length })}
                  </Button>
                </div>
              ) : null}
              {scan.data.groups.map((group, index) => (
                <DuplicateGroupCard
                  key={group.members.map((row) => row.id).join('-')}
                  group={group}
                  groupIndex={index}
                  canManage={canManage}
                  canDelete={canDelete}
                  onRefresh={refresh}
                />
              ))}
            </div>
          )}
        </>
      ) : null}

      {!shouldRun ? (
        <EmptyState
          icon={<Search className="size-8" aria-hidden />}
          title={t('duplicates.notRunTitle')}
          body={t('duplicates.notRunDescription')}
        />
      ) : null}

      <ConfirmDialog
        open={mergeAllOpen}
        onOpenChange={setMergeAllOpen}
        title={t('duplicates.mergeAllConfirmTitle')}
        description={t('duplicates.mergeAllConfirmDescription', {
          groups: formatNumber(scan.data?.groups.length ?? 0),
          records: formatNumber(mergePairs.length),
        })}
        confirmLabel={t('duplicates.mergeAllAction')}
        cancelLabel={tCommon('action.cancel')}
        onConfirm={() => bulkMerge.mutate(mergePairs)}
        loading={bulkMerge.isPending}
      />
    </div>
  );
};
